/**
 * An agent's tool calls read as one line of prose per burst — "Ran 6 commands, edited 3 files" —
 * because a chat where every `git status` takes three lines of output is a log, not a
 * conversation. The calls themselves stay one tap away.
 */

/** What the worker stores on a `tool` message (`scripts/jobs/agents.ts`). */
export interface ToolCall {
  name: string;
  body: string;
  /** Pretty-printed JSON, clipped at 4,000 characters — so it may not parse. */
  input?: string;
  status: "running" | "done" | "error";
  result?: string;
}

export function toolCallOf(message: { body: string; data: Record<string, unknown> | null }): ToolCall {
  const data = message.data ?? {};
  const status = data.status === "done" || data.status === "error" ? data.status : "running";
  return {
    name: typeof data.name === "string" ? data.name : message.body.replace(/\(.*$/s, "").trim(),
    body: message.body,
    input: typeof data.input === "string" ? data.input : undefined,
    status,
    result: typeof data.result === "string" ? data.result : undefined,
  };
}

export type ChatItem<T> = { type: "tools"; key: string; messages: T[] } | { type: "message"; key: string; message: T };

/**
 * Runs of consecutive tool messages become one item, keyed by the first call's id, so a group the
 * user opened stays open while more calls stream onto its end.
 */
export function groupToolRuns<T extends { id: string; kind: string }>(messages: T[]): ChatItem<T>[] {
  const items: ChatItem<T>[] = [];
  for (const message of messages) {
    const last = items[items.length - 1];
    if (message.kind !== "tool") items.push({ type: "message", key: message.id, message });
    else if (last?.type === "tools") last.messages.push(message);
    else items.push({ type: "tools", key: message.id, messages: [message] });
  }
  return items;
}

type Category = "bash" | "edit" | "write" | "read" | "search" | "web" | "board";
const ORDER: Category[] = ["bash", "edit", "write", "read", "search", "web", "board"];

function categoryOf(name: string): Category | null {
  if (name === "Bash" || name === "BashOutput") return "bash";
  if (name === "Edit" || name === "MultiEdit" || name === "NotebookEdit") return "edit";
  if (name === "Write") return "write";
  if (name === "Read" || name === "NotebookRead") return "read";
  if (name === "Glob" || name === "Grep" || name === "LS") return "search";
  if (name === "WebFetch" || name === "WebSearch") return "web";
  if (name.startsWith("mcp__board__")) return "board";
  return null;
}

/** The argument `describeTool` put in the parentheses: a command, a path, a pattern. */
function argOf(body: string): string {
  return body.match(/^[^(]*\((.*)\)$/s)?.[1] ?? "";
}

/** The file a call touched, from its input when that still parses, else from its one-line body. */
function fileOf(call: ToolCall): string {
  if (call.input) {
    try {
      const input = JSON.parse(call.input) as Record<string, unknown>;
      const path = input.file_path ?? input.notebook_path ?? input.path;
      if (typeof path === "string" && path) return path;
    } catch {
      const match = call.input.match(/"(?:file_path|notebook_path)":\s*"((?:[^"\\]|\\.)*)"/);
      if (match?.[1]) return match[1];
    }
  }
  return argOf(call.body) || call.body;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : `${n} ${many}`);

function phrase(category: Category, calls: ToolCall[]): string {
  const files = () => new Set(calls.map(fileOf)).size;
  switch (category) {
    case "bash":
      return `ran ${plural(calls.length, "a command", "commands")}`;
    case "edit":
      return `edited ${plural(files(), "a file", "files")}`;
    case "write":
      return `wrote ${plural(files(), "a file", "files")}`;
    case "read":
      return `read ${plural(files(), "a file", "files")}`;
    case "search":
      return calls.length === 1 ? "searched once" : `searched ${calls.length} times`;
    case "web":
      return plural(calls.length, "a web lookup", "web lookups");
    case "board":
      return plural(calls.length, "a board action", "board actions");
  }
}

/** A tool with no category of its own: `TodoWrite`, or `mcp__github__get_pr` as `github: get_pr`. */
function otherName(name: string): string {
  return name.replace(/^mcp__([^_]+(?:_[^_]+)*)__/, "$1: ");
}

/** "Ran 6 commands, edited 3 files, read 2 files, searched 4 times, 3 board actions". */
export function summarizeTools(calls: ToolCall[]): string {
  const byCategory = new Map<Category, ToolCall[]>();
  const others = new Map<string, number>();
  for (const call of calls) {
    const category = categoryOf(call.name);
    if (category) byCategory.set(category, [...(byCategory.get(category) ?? []), call]);
    else others.set(call.name, (others.get(call.name) ?? 0) + 1);
  }
  const parts = ORDER.filter((category) => byCategory.has(category)).map((category) => phrase(category, byCategory.get(category)!));
  for (const [name, count] of others) parts.push(`used ${otherName(name)}${count > 1 ? ` ${count} times` : ""}`);
  const text = parts.join(", ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Used a tool";
}

/** The short "what" beside a single call's summary: the command, the path, the board tool. */
export function callDetail(call: ToolCall): string {
  if (call.name.startsWith("mcp__board__")) return call.body.replace(/^board: /, "");
  return argOf(call.body);
}

/** A subagent `Task` call read as one line: its description, with its type when it carries one. */
export function subagentLabel(call: ToolCall): string {
  if (call.input) {
    try {
      const input = JSON.parse(call.input) as Record<string, unknown>;
      const desc = typeof input.description === "string" ? input.description.trim() : "";
      const type = typeof input.subagent_type === "string" ? input.subagent_type.trim() : "";
      if (desc) return type ? `${desc} · ${type}` : desc;
      if (type) return type;
    } catch {
      // The stored input is clipped at 4,000 chars, so it may not parse — fall back to the body.
    }
  }
  return argOf(call.body) || "a subagent";
}
